// frontend/src/lib/api/quickBookingService.ts
import { BaseApiService } from './baseService';
import { ApiResponse, httpClient } from '../httpClient';
import type { 
  ServiceDetails, 
  QuickBookingRequest, 
  QuickBookingResponse 
} from '@/types/quickBooking';

// Re-export types for convenience
export type { ServiceDetails, QuickBookingRequest, QuickBookingResponse } from '@/types/quickBooking';

// Removed - now imported from types file

export interface ProviderQuote {
  quote_id: string;
  provider_id: number;
  provider_name: string;
  business_name: string;
  price: number;
  estimated_duration: string;
  availability: string;
  description: string;
  rating: number;
  reviews_count: number;
}

export interface BookingConfirmation {
  booking_id: number;
  provider: {
    name: string;
    phone: string;
    address: string;
  };
  scheduled_date: string;
  estimated_cost: number;
  reference_number: string;
}

class QuickBookingService extends BaseApiService {
  constructor() {
    super({
      baseEndpoint: '/quick-booking',
      timeout: 10000,
    });
  }

  async getAvailableServices(location?: string, categoryId?: number): Promise<ApiResponse<ServiceDetails[]>> {
    return this.get('/services', { params: { location, category_id: categoryId } });
  }

  async getServiceDetails(serviceId: number): Promise<ApiResponse<ServiceDetails>> {
    return this.get(`/services/${serviceId}`);
  }

  async getServiceByCode(serviceCode: string): Promise<ApiResponse<ServiceDetails>> {
    // Use absolute path to bypass base endpoint for this public API
    // Note: httpClient already has /api as base URL, so we just need /public/...
    const config = {
      method: 'GET' as const,
      url: `/public/services/by-code/${serviceCode}`,
      timeout: this.timeout,
      headers: { skipAuth: 'true' }
    };
    
    try {
      const response = await httpClient.request(config);
      return response.data;
    } catch (error: any) {
      console.error(`API GET /public/services/by-code/${serviceCode} failed:`, error);
      
      if (error.response?.data) {
        return error.response.data;
      }
      
      return {
        success: false,
        message: error.message || 'An error occurred',
      };
    }
  }

  async getAvailableProviders(serviceId: number, location: string, filters: any = {}): Promise<ApiResponse<any>> {
    return this.get('/providers', { params: { service_id: serviceId, location, ...filters } });
  }

  async checkAvailability(serviceId: number, location: string, dateTime: string, duration?: number): Promise<ApiResponse<any>> {
    return this.post('/check-availability', { service_id: serviceId, location, date_time: dateTime, duration });
  }

  async getPricingEstimate(serviceId: number, location: string, requirements?: any): Promise<ApiResponse<any>> {
    return this.post('/pricing-estimate', { service_id: serviceId, location, requirements });
  }

  async submitQuickBooking(bookingData: QuickBookingRequest): Promise<ApiResponse<QuickBookingResponse>> {
    return this.post('/authenticated', bookingData);
  }

  async getBookingRequest(requestId: string): Promise<ApiResponse<QuickBookingResponse>> {
    return this.get(`/requests/${requestId}`);
  }

  async getUserBookingRequests(filters: any = {}): Promise<ApiResponse<any>> {
    return this.get('/my-requests', { params: filters });
  }

  async cancelBookingRequest(requestId: string, reason?: string): Promise<ApiResponse<any>> {
    return this.post(`/requests/${requestId}/cancel`, { reason });
  }

  async getProviderQuotes(requestId: string): Promise<ApiResponse<ProviderQuote[]>> {
    return this.get(`/requests/${requestId}/quotes`);
  }

  async acceptProviderQuote(quoteId: string): Promise<ApiResponse<BookingConfirmation>> {
    return this.post(`/quotes/${quoteId}/accept`);
  }

  async declineProviderQuote(quoteId: string, reason?: string): Promise<ApiResponse<any>> {
    return this.post(`/quotes/${quoteId}/decline`, { reason });
  }

  async getInstantBookingOptions(serviceId: number, location: string): Promise<ApiResponse<any>> {
    return this.get('/instant-options', { params: { service_id: serviceId, location } });
  }

  async createInstantBooking(bookingData: any): Promise<ApiResponse<BookingConfirmation>> {
    return this.post('/instant-booking', bookingData);
  }

  async searchServices(searchQuery: string, location?: string): Promise<ApiResponse<ServiceDetails[]>> {
    return this.get('/services/search', { params: { q: searchQuery, location } });
  }

  async getPopularServices(location: string, limit: number = 10): Promise<ApiResponse<ServiceDetails[]>> {
    return this.get('/services/popular', { params: { location, limit } });
  }

  async getServiceCategories(): Promise<ApiResponse<any>> {
    return this.get('/categories');
  }

  async trackBookingProgress(requestId: string): Promise<ApiResponse<any>> {
    return this.get(`/requests/${requestId}/progress`);
  }

  async rateBooking(requestId: string, rating: number, review?: string, providerId?: number): Promise<ApiResponse<any>> {
    return this.post(`/requests/${requestId}/rate`, { rating, review, provider_id: providerId });
  }

  async getBookingHistory(filters: any = {}): Promise<ApiResponse<any>> {
    return this.get('/history', { params: filters });
  }

  async checkAreaAvailability(pincode: string, serviceType?: string): Promise<ApiResponse<{
    available: boolean;
    providers_count: number;
    estimated_response_time?: string;
  }>> {
    return this.post('/check-area', { pincode, service_type: serviceType });
  }


// ✅ NEW: Submit authenticated booking method
async submitAuthenticatedBooking(bookingData: QuickBookingRequest): Promise<ApiResponse<any>> {
  return this.post('/authenticated', bookingData);
}

// ✅ NEW: Get booking by reference method
async getBookingByReference(reference: string): Promise<ApiResponse<any>> {
  return this.get(`/booking/${reference}`);
}

// ✅ NEW: Search services method


}


export const quickBookingService = new QuickBookingService();